import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getTherapistRequests } from "../../redux/requestSlice";
import { getUserTestResults } from "../../redux/testSlice";
import TherapistSideBar from "../../common/TherapistSidebar/TherapistSidebar";
import UserPage from "../../common/UserPage/UserPage";
import Footer from "../../common/Footer/Footer";
import { FiUser, FiFileText, FiChevronDown, FiChevronUp, FiLoader } from "react-icons/fi";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

const ACTIVE_STATUSES = new Set(["accepted", "confirmed", "active", "ongoing", "completed"]);

const getClientInfo = (req) => {
  const u = req.userId;
  if (!u) return null;
  const id = typeof u === "object" ? u._id || u.id : u;
  const name = typeof u === "object"
    ? `${u.name || ""} ${u.surname || ""}`.trim() || u.email || "Client"
    : String(u);
  const email = typeof u === "object" ? u.email || "" : "";
  return { id: String(id), name, email };
};

const ScoreBar = ({ value, max }) => {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 bg-gray-100 rounded-full h-2">
        <div
          className="h-2 rounded-full bg-[#7a4f6a] transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs text-gray-500 whitespace-nowrap">{value} / {max}</span>
    </div>
  );
};

const ClientTestResults = ({ clientId }) => {
  const [results, setResults] = useState(null);
  const [testQuestionMap, setTestQuestionMap] = useState({}); // testId → { questions, questionsById }
  const [loading, setLoading] = useState(true);
  const [expandedTest, setExpandedTest] = useState(null);

  useEffect(() => {
    if (!clientId) return;
    const token = localStorage.getItem("token");
    setLoading(true);
    axios
      .get(`${API_URL}/tests/users/${clientId}/test-results`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(async (res) => {
        const data = res.data?.data || res.data || [];
        const arr = Array.isArray(data) ? data : [];
        setResults(arr);

        // Her test için soru verilerini çek
        const uniqueTestIds = [...new Set(arr.map((r) => r.testId?._id || r.testId).filter(Boolean))];
        const map = {};
        await Promise.all(
          uniqueTestIds.map((tid) =>
            axios
              .get(`${API_URL}/tests/${tid}`, { headers: { Authorization: `Bearer ${token}` } })
              .then((r) => {
                const questions = r.data?.data?.questions || r.data?.questions || [];
                const byId = {};
                questions.forEach((q) => { byId[q._id] = q; });
                map[String(tid)] = { questions, byId };
              })
              .catch(() => {})
          )
        );
        setTestQuestionMap(map);
      })
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  }, [clientId]);

  // Sayısal cevabı seçenek metniyle çöz
  const resolveAnswer = (resp, testId) => {
    const raw = resp.answer;
    const qId = typeof resp.questionId === "object" ? resp.questionId?._id : resp.questionId;
    const testData = testQuestionMap[String(testId)];
    const question = testData?.byId?.[qId];

    if (question?.options?.length > 0) {
      // value eşleşmesine bak
      const match = question.options.find((o) => String(o.value) === String(raw));
      if (match?.text) return match.text;
      // index eşleşmesine bak (0 → ilk seçenek)
      if (typeof raw === "number" && question.options[raw]?.text) return question.options[raw].text;
    }
    // Scale veya text tipi — sayıyı olduğu gibi göster
    return typeof raw === "object" ? JSON.stringify(raw) : String(raw ?? "");
  };

  const resolveQuestion = (resp, testId) => {
    if (resp.questionId?.questionText) return resp.questionId.questionText;
    const qId = typeof resp.questionId === "object" ? resp.questionId?._id : resp.questionId;
    const testData = testQuestionMap[String(testId)];
    return testData?.byId?.[qId]?.questionText || resp.question || null;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <FiLoader className="w-5 h-5 text-[#7a4f6a] animate-spin" />
      </div>
    );
  }

  if (!results || results.length === 0) {
    return (
      <p className="text-sm text-gray-400 text-center py-6">
        No test results found for this client.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {results.map((result, i) => {
        const testName = result.testId?.name || result.testName || `Test ${i + 1}`;
        const responses = result.responses || [];
        const score = result.score ?? result.totalScore ?? null;
        const maxScore = result.maxScore ?? result.testId?.maxScore ?? null;
        const isExpanded = expandedTest === i;
        const date = result.createdAt
          ? new Date(result.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
          : "";

        const testId = result.testId?._id || result.testId;

        return (
          <div key={i} className="border border-gray-100 rounded-xl overflow-hidden">
            <button
              onClick={() => setExpandedTest(isExpanded ? null : i)}
              className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
            >
              <div className="flex items-center gap-2 min-w-0">
                <FiFileText className="w-4 h-4 text-[#7a4f6a] shrink-0" />
                <span className="text-sm font-semibold text-gray-800 truncate">{testName}</span>
                {date && <span className="text-xs text-gray-400 shrink-0">{date}</span>}
              </div>
              <div className="flex items-center gap-3 shrink-0 ml-2">
                {score !== null && maxScore !== null && (
                  <span className="text-xs font-medium text-[#7a4f6a] bg-[#E5D3DD]/50 px-2 py-0.5 rounded-full">
                    {score}/{maxScore}
                  </span>
                )}
                {isExpanded ? <FiChevronUp className="w-4 h-4 text-gray-400" /> : <FiChevronDown className="w-4 h-4 text-gray-400" />}
              </div>
            </button>

            {isExpanded && (
              <div className="px-4 py-3 space-y-3 bg-white">
                {score !== null && maxScore !== null && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Score</p>
                    <ScoreBar value={score} max={maxScore} />
                  </div>
                )}
                {result.interpretation && (
                  <div className="p-3 bg-[#E5D3DD]/20 rounded-lg">
                    <p className="text-xs font-semibold text-[#7a4f6a] mb-1">Interpretation</p>
                    <p className="text-sm text-gray-700">{result.interpretation}</p>
                  </div>
                )}
                {responses.length > 0 && (
                  <div className="space-y-3">
                    <p className="text-xs font-semibold text-gray-500">Responses</p>
                    {responses.map((resp, j) => {
                      const questionText = resolveQuestion(resp, testId);
                      const answerText = resolveAnswer(resp, testId);
                      return (
                        <div key={j} className="text-sm">
                          <p className="text-gray-600 font-medium">
                            {questionText || `Question ${j + 1}`}
                          </p>
                          <p className="text-gray-800 mt-0.5 pl-3 border-l-2 border-[#E5D3DD]">
                            {answerText}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const TherapistClientReportsPage = () => {
  const dispatch = useDispatch();
  const { requests = [], loading } = useSelector((state) => state.requests);
  const therapistRequests = useSelector((state) => state.therapist?.therapist?.requests);
  const picture = useSelector((state) => state.therapist?.therapist?.picture);

  const [selectedClientId, setSelectedClientId] = useState(null);

  useEffect(() => {
    dispatch(getTherapistRequests());
  }, [dispatch]);

  const allRequests = useMemo(() => {
    const merged = Array.isArray(requests) ? [...requests] : [];
    (therapistRequests || []).forEach((r) => {
      const id = r._id || r.id;
      if (id && !merged.find((m) => (m._id || m.id) === id)) merged.push(r);
    });
    return merged;
  }, [requests, therapistRequests]);

  // Benzersiz danışanları bul (active/completed randevusu olanlar)
  const clients = useMemo(() => {
    const map = new Map();
    allRequests.forEach((req) => {
      if (!ACTIVE_STATUSES.has((req.status || "").toLowerCase())) return;
      const info = getClientInfo(req);
      if (!info) return;
      if (!map.has(info.id)) map.set(info.id, info);
    });
    return Array.from(map.values());
  }, [allRequests]);

  const selectedClient = clients.find((c) => c.id === selectedClientId);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <UserPage customImage={picture} />

      <div className="flex flex-col md:flex-row flex-1 w-full p-2 mt-[-20px] gap-0 md:gap-4 items-start">
        <div className="flex-shrink-0 w-full md:w-auto">
          <TherapistSideBar />
        </div>

        <div className="flex-1 min-w-0 mt-2 md:mt-6 px-2 md:px-0">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">Client Reports</h1>

          {loading ? (
            <div className="flex justify-center py-16">
              <FiLoader className="w-6 h-6 text-[#7a4f6a] animate-spin" />
            </div>
          ) : clients.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
              <FiUser className="w-10 h-10 text-gray-200 mx-auto mb-3" />
              <p className="text-gray-400 text-sm">No clients with active sessions yet.</p>
            </div>
          ) : (
            <div className="flex gap-4 flex-col md:flex-row">
              {/* Danışan listesi */}
              <div className="w-full md:w-64 shrink-0">
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-semibold text-gray-700">Clients ({clients.length})</p>
                  </div>
                  <div className="divide-y divide-gray-50">
                    {clients.map((client) => (
                      <button
                        key={client.id}
                        onClick={() => setSelectedClientId(client.id)}
                        className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors ${
                          selectedClientId === client.id ? "bg-[#E5D3DD]/40" : "hover:bg-gray-50"
                        }`}
                      >
                        <div className="w-9 h-9 rounded-full bg-[#E5D3DD] flex items-center justify-center text-sm font-semibold text-[#7a4f6a] shrink-0">
                          {client.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className={`text-sm font-medium truncate ${selectedClientId === client.id ? "text-[#7a4f6a]" : "text-gray-700"}`}>
                            {client.name}
                          </p>
                          {client.email && (
                            <p className="text-xs text-gray-400 truncate">{client.email}</p>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Test sonuçları paneli */}
              <div className="flex-1 min-w-0">
                {selectedClient ? (
                  <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                    <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-100">
                      <div className="w-10 h-10 rounded-full bg-[#E5D3DD] flex items-center justify-center text-sm font-semibold text-[#7a4f6a]">
                        {selectedClient.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{selectedClient.name}</p>
                        {selectedClient.email && (
                          <p className="text-xs text-gray-400">{selectedClient.email}</p>
                        )}
                      </div>
                    </div>
                    <ClientTestResults clientId={selectedClient.id} />
                  </div>
                ) : (
                  <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-12 flex flex-col items-center justify-center text-center">
                    <FiFileText className="w-10 h-10 text-gray-200 mb-3" />
                    <p className="text-gray-400 text-sm">Select a client to view their test results.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TherapistClientReportsPage;
